import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { apiClient } from '../lib/api/client';
import { Button } from '../components/ui/Button';
import { PasswordInput } from '../components/ui/PasswordInput';
import { Alert, AlertDescription } from '../components/ui/Alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Key, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

export const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);

  useEffect(() => {
    console.log('🔄 ResetPassword: Token from URL:', token ? 'present' : 'missing');
    if (!token) {
      console.log('❌ ResetPassword: No token found in URL');
      setTokenValid(false);
      setError('Invalid reset link. Please request a new password reset.');
    } else {
      // Token exists, validate it by making a test call or just set as valid
      // We'll let the backend validate the token when the form is submitted
      console.log('✅ ResetPassword: Token found, setting tokenValid to true');
      setTokenValid(true);
      setError(null);
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate token exists
    if (!token) {
      setError('Invalid reset token. Please request a new password reset link.');
      setTokenValid(false);
      return;
    }

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength (basic check)
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      console.log('🔄 ResetPassword: Submitting password reset with token:', token ? 'present' : 'missing');
      await apiClient.resetPassword(token, password);
      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      console.error('❌ ResetPassword: Error resetting password:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to reset password. The link may have expired. Please request a new one.';
      setError(errorMessage);
      if (err.response?.status === 400) {
        // Token expired or invalid
        setTokenValid(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Only show error page if token is explicitly missing (not just invalid)
  // Allow form to render even if tokenValid is false initially, so user can try submitting
  if (tokenValid === false && !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background-200 dark:from-[#141414] via-background-100 dark:via-[#1A1A1A] to-background-200 dark:to-[#141414] p-4">
        <Card className="w-full max-w-md glass-card border-glass-border/40 shadow-2xl">
          <CardHeader className="space-y-1">
            <Link
              to="/login"
              className="flex items-center text-sm text-text-enterprise-tertiary hover:text-text-enterprise-primary transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Login
            </Link>
            <CardTitle className="text-2xl font-bold text-text-enterprise-primary">
              Invalid Reset Link
            </CardTitle>
            <CardDescription className="text-text-enterprise-tertiary">
              This password reset link is invalid or has expired.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg mb-4">
              <AlertCircle className="w-12 h-12 text-red-500" />
            </div>
            <div className="space-y-4">
              <p className="text-sm text-text-enterprise-tertiary text-center">
                Password reset links expire after 1 hour. Please request a new password reset link.
              </p>
              <Button
                onClick={() => navigate('/password/forgot')}
                variant="gradient"
                className="w-full"
              >
                Request New Reset Link
              </Button>
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="w-full"
              >
                Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background-200 dark:from-[#141414] via-background-100 dark:via-[#1A1A1A] to-background-200 dark:to-[#141414] p-4">
      <Card className="w-full max-w-md glass-card border-glass-border/40 shadow-2xl">
        <CardHeader className="space-y-1">
          <Link
            to="/login"
            className="flex items-center text-sm text-text-enterprise-tertiary hover:text-text-enterprise-primary transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Login
          </Link>
          <CardTitle className="text-2xl font-bold text-text-enterprise-primary">
            Reset Password
          </CardTitle>
          <CardDescription className="text-text-enterprise-tertiary">
            Enter your new password below.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="space-y-4">
              <div className="flex items-center justify-center p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-lg font-semibold text-text-enterprise-primary">
                  Password Reset Successful!
                </h3>
                <p className="text-sm text-text-enterprise-tertiary">
                  Your password has been reset successfully. Redirecting to login...
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-text-enterprise-primary">
                  New Password
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-enterprise-tertiary z-10" />
                  <PasswordInput
                    id="password"
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="pl-10"
                  />
                </div>
                <p className="text-xs text-text-enterprise-tertiary">
                  Must be at least 8 characters with uppercase, lowercase, number, and special character
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="text-sm font-medium text-text-enterprise-primary">
                  Confirm Password
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-enterprise-tertiary z-10" />
                  <PasswordInput
                    id="confirmPassword"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="pl-10"
                  />
                </div>
              </div>

              <Button
                type="submit"
                variant="gradient"
                disabled={isLoading || !password || !confirmPassword}
                className="w-full"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <LoadingSpinner size="sm" variant="ai" />
                    Resetting Password...
                  </span>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

